import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  CopyIcon,
  TerminalIcon,
  ChevronDownIcon,
  ServerIcon,
  PlayIcon,
  SquareIcon,
  RotateCcwIcon,
  MonitorIcon,
  CpuIcon,
  HardDriveIcon,
  DatabaseIcon,
  CheckCircleIcon,
  ClockIcon,
  AlertCircleIcon,
  GlobeIcon,
  NetworkIcon,
  KeyIcon,
  RefreshCwIcon,
  Loader2Icon,
  AlertTriangleIcon,
  Trash2Icon,
  CameraIcon,
  ShieldIcon,
  Maximize2Icon,
  Minimize2Icon,
  PencilIcon,
  CheckIcon,
  XIcon,
  DownloadIcon,
  InfoIcon,
} from 'lucide-react'
import { useState, useMemo, useRef, useEffect } from 'react'
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { ResponsiveActionRow } from '@nebula/shared/components'
import { ConfirmModal } from '@nebula/shared/components/modals'
import {
  backupsApi,
  firewallApi,
  formatBytes as formatBackupBytes,
  formatDuration as formatBackupDuration,
  getBackupStatusLabel,
  type BackupSnapshot,
} from '@nebula/shared/services'
import { apiClient } from '@nebula/shared/services/api'
import blockStorageApi, { getVolumeStatusLabel } from '@nebula/shared/services/blockStorageApi'
import { consoleApi } from '@nebula/shared/services/consoleApi'
import { getRDPGatewayStatus } from '@nebula/shared/services/gatewayApi'
import instancesApi, {
  VMInstanceDetails,
  type RRDDataPoint,
} from '@nebula/shared/services/instancesApi'
import {
  getApiErrorText,
  isBackupConcluido,
  FILTRO_DE_BACKUP_CONCLUIDO,
  AVISO_PROVISIONAMENTO_INCOMPLETO,
  provisionamentoIncompleto,
  desdeQuandoIncompleto,
} from '@nebula/shared/utils'
import { formatRelativeTime, parseApiDate } from '@nebula/shared/utils/format'
import {
  ehWindowsParaApresentacao,
  ehWindowsParaCredenciais,
} from '@nebula/shared/utils/vmOsFamily'

import SerialConsole from '../components/SerialConsole'
import FirewallTab from '../components/VMDetail/FirewallTab'
// Reuse existing VMDetail tab components
import InstanceCredentialsCard from '../components/VMDetail/InstanceCredentialsCard'
import NetworkTab from '../components/VMDetail/NetworkTab'
import SnapshotsTab from '../components/VMDetail/SnapshotsTab'
import {
  autorDoEvento,
  descreveEvento,
  type NivelDoEvento,
} from '../components/VMDetail/vmHistoryLabels'
import VNCConsole from '../components/VNCConsole'

/**
 * Endereço pelo qual o cliente alcança a VM de fora (issue #733).
 *
 * O IPv4 público tem precedência sobre a lista do guest agent, que não serve como
 * desempate: ela já traz o IP privado do IPAM quando o agent silencia (VM parada) e
 * inclui qualquer interface não-loopback (docker0, VPN, 2ª NIC de VPC) na ordem em que o
 * guest devolveu. Uma régua só, usada no card do topo e nos comandos de acesso — antes o
 * card podia anunciar o público enquanto o SSH copiado apontava para o `docker0`.
 */
export function resolveReachableIp(instance: VMInstanceDetails): string | null {
  return instance.public_ip?.ip_address || instance.proxmox_details?.ip_addresses?.[0] || null
}

const PERIOD_OPTIONS: { label: string; timeframe: string }[] = [
  { label: '1 hora', timeframe: 'hour' },
  { label: '1 dia', timeframe: 'day' },
  { label: '7 dias', timeframe: 'week' },
]

type NavId =
  | 'graphs'
  | 'access'
  | 'power'
  | 'resources'
  | 'networking'
  | 'firewall'
  | 'backups'
  | 'snapshots'
  | 'history'
  | 'rebuild'
  | 'destroy'

const DEFAULT_NAV: NavId = 'graphs'

const SIDE_NAV_ITEMS: {
  id: NavId
  label: string
  icon: typeof MonitorIcon
  danger?: boolean
  warning?: boolean
}[] = [
  { id: 'graphs', label: 'Gráficos', icon: MonitorIcon },
  { id: 'access', label: 'Acesso', icon: KeyIcon },
  { id: 'power', label: 'Power', icon: PlayIcon },
  { id: 'resources', label: 'Recursos', icon: CpuIcon },
  { id: 'networking', label: 'Rede', icon: NetworkIcon },
  { id: 'firewall', label: 'Firewall', icon: ShieldIcon },
  { id: 'backups', label: 'Backups', icon: HardDriveIcon },
  { id: 'snapshots', label: 'Snapshots', icon: CameraIcon },
  { id: 'history', label: 'Histórico', icon: ClockIcon },
  { id: 'rebuild', label: 'Rebuild', icon: RefreshCwIcon, warning: true },
  { id: 'destroy', label: 'Destruir', icon: Trash2Icon, danger: true },
]

const NAV_IDS = new Set<string>(SIDE_NAV_ITEMS.map((item) => item.id))

/**
 * Nomes antigos de aba que ainda circulam em links salvos e na documentação (#759).
 * Sem isso, `?tab=resize` cairia no default (Gráficos) e o cliente teria de procurar a
 * aba de novo.
 */
const NAV_ALIASES: Record<string, NavId> = { resize: 'resources' }

const STATUS_CONFIG: Record<
  string,
  { color: string; icon: typeof CheckCircleIcon; label: string }
> = {
  running: {
    color: 'bg-emerald-50 text-emerald-700 border border-emerald-200/60',
    icon: CheckCircleIcon,
    label: 'Executando',
  },
  stopped: {
    color: 'bg-slate-100 text-slate-600 border border-slate-200/60',
    icon: SquareIcon,
    label: 'Parada',
  },
  creating: {
    color: 'bg-amber-50 text-amber-700 border border-amber-200/60',
    icon: ClockIcon,
    label: 'Criando',
  },
  rebuilding: {
    color: 'bg-amber-50 text-amber-700 border border-amber-200/60',
    icon: RefreshCwIcon,
    label: 'Reconstruindo',
  },
  error: {
    color: 'bg-red-50 text-red-700 border border-red-200/60',
    icon: AlertCircleIcon,
    label: 'Erro',
  },
}

// ── Chart helpers ──────────────────────────────────────────
function MiniAreaChart({
  series,
  height = 200,
  yLabel,
  xLabels,
}: {
  series: { data: number[]; color: string; label: string }[]
  height?: number
  yLabel?: string
  xLabels?: string[]
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(920)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(Math.round(entry.contentRect.width))
      }
    })
    ro.observe(el)
    setContainerWidth(el.clientWidth)
    return () => ro.disconnect()
  }, [])

  const count = series[0]?.data.length || 0
  if (count < 2)
    return (
      <div
        ref={containerRef}
        className="flex h-[200px] items-center justify-center text-xs text-slate-400"
      >
        Sem dados suficientes
      </div>
    )
  const allValues = series.flatMap((s) => s.data)
  const maxVal = Math.max(...allValues, 0.001) * 1.15
  const w = containerWidth,
    h = height
  const padL = 60,
    padR = 20,
    padT = 10,
    padB = 30
  const chartW = w - padL - padR,
    chartH = h - padT - padB
  const toX = (i: number) => padL + (i / (count - 1)) * chartW
  const toY = (v: number) => padT + chartH - (v / maxVal) * chartH
  const gridLines = 4
  const yTicks = Array.from({ length: gridLines + 1 }, (_, i) => (i / gridLines) * maxVal)

  return (
    <div ref={containerRef}>
      <svg width={w} height={h}>
        {yTicks.map((v, i) => (
          <g key={i}>
            <line
              x1={padL}
              x2={w - padR}
              y1={toY(v)}
              y2={toY(v)}
              className="stroke-slate-200"
              strokeDasharray="3 3"
              strokeWidth={1}
            />
            <text
              x={padL - 8}
              y={toY(v) + 3}
              textAnchor="end"
              className="fill-slate-400 text-[10px]"
            >
              {v < 1 ? v.toFixed(2) : v < 10 ? v.toFixed(1) : Math.round(v)}
              {yLabel ? ` ${yLabel}` : ''}
            </text>
          </g>
        ))}
        {xLabels?.map((label, i) => {
          const step = Math.floor(count / (xLabels.length - 1))
          return (
            <text
              key={i}
              x={toX(Math.min(i * step, count - 1))}
              y={h - 4}
              textAnchor="middle"
              className="fill-slate-400 text-[10px]"
            >
              {label}
            </text>
          )
        })}
        {series.map((s, si) => {
          const linePath = s.data
            .map((v, i) => `${i === 0 ? 'M' : 'L'}${toX(i)},${toY(v)}`)
            .join(' ')
          const areaPath = `${linePath} L${toX(count - 1)},${toY(0)} L${toX(0)},${toY(0)} Z`
          return (
            <g key={si}>
              <path d={areaPath} fill={s.color} opacity={0.1} />
              <path d={linePath} fill="none" stroke={s.color} strokeWidth={1.5} />
            </g>
          )
        })}
      </svg>
    </div>
  )
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      }}
      className="ml-1.5 inline-flex items-center text-slate-400 transition-colors hover:text-slate-600"
      title="Copiar"
    >
      <CopyIcon size={13} className={copied ? 'text-emerald-500' : ''} />
    </button>
  )
}

function formatUptime(seconds: number): string {
  if (!seconds) return '—'
  const d = Math.floor(seconds / 86400),
    h = Math.floor((seconds % 86400) / 3600),
    m = Math.floor((seconds % 3600) / 60)
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

// ════════════════════════════════════════════════════════════
// Tab Content Components
// ════════════════════════════════════════════════════════════

function GraphsSection({ instanceId }: { instanceId: string }) {
  const [selectedPeriod, setSelectedPeriod] = useState(PERIOD_OPTIONS[0])
  const [periodOpen, setPeriodOpen] = useState(false)

  const { data: rrdResponse, isLoading } = useQuery({
    queryKey: ['rrddata', instanceId, selectedPeriod.timeframe],
    queryFn: () => instancesApi.getMetricasDaVM(instanceId, selectedPeriod.timeframe),
    refetchInterval: selectedPeriod.timeframe === 'hour' ? 60000 : undefined,
  })

  const rrdData = rrdResponse?.data || []

  // Build time labels from RRD timestamps
  const xLabels = useMemo(() => {
    if (rrdData.length === 0) return []
    const count = Math.min(7, rrdData.length)
    const step = Math.max(1, Math.floor(rrdData.length / (count - 1)))
    return Array.from({ length: count }, (_, i) => {
      const idx = Math.min(i * step, rrdData.length - 1)
      const entry = rrdData[idx]
      if (!entry?.time) return ''
      const t = new Date(entry.time * 1000)
      if (selectedPeriod.timeframe === 'hour' || selectedPeriod.timeframe === 'day') {
        return `${t.getHours().toString().padStart(2, '0')}:${t.getMinutes().toString().padStart(2, '0')}`
      }
      return `${t.getDate().toString().padStart(2, '0')}/${(t.getMonth() + 1).toString().padStart(2, '0')}`
    })
  }, [rrdData, selectedPeriod.timeframe])

  // Extract series from RRD data
  const graphs = useMemo(() => {
    if (rrdData.length === 0) return []

    const cpuData = rrdData.map((d: RRDDataPoint) => {
      const maxcpu = d.maxcpu || 1
      return ((d.cpu || 0) / maxcpu) * 100
    })

    const memData = rrdData.map((d: RRDDataPoint) => {
      const maxmem = d.maxmem || 1
      return ((d.mem || 0) / maxmem) * 100
    })

    const netInData = rrdData.map((d: RRDDataPoint) => (d.netin || 0) / 1024) // KB/s
    const netOutData = rrdData.map((d: RRDDataPoint) => (d.netout || 0) / 1024)

    const diskReadData = rrdData.map((d: RRDDataPoint) => (d.diskread || 0) / 1024) // KB/s
    const diskWriteData = rrdData.map((d: RRDDataPoint) => (d.diskwrite || 0) / 1024)

    return [
      {
        title: 'CPU',
        yLabel: '%',
        series: [{ data: cpuData, color: '#0ea5e9', label: 'CPU' }],
      },
      {
        title: 'Memória',
        yLabel: '%',
        series: [{ data: memData, color: '#8b5cf6', label: 'Memória' }],
      },
      {
        title: 'Rede',
        yLabel: 'KB/s',
        series: [
          { data: netInData, color: '#0ea5e9', label: 'entrada' },
          { data: netOutData, color: '#10b981', label: 'saída' },
        ],
      },
      {
        title: 'Disco I/O',
        yLabel: 'KB/s',
        series: [
          { data: diskReadData, color: '#0ea5e9', label: 'leitura' },
          { data: diskWriteData, color: '#10b981', label: 'escrita' },
        ],
      },
    ]
  }, [rrdData])

  return (
    <>
      <div className="mb-6 rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
            Período
          </label>
          <div className="relative" style={{ width: 170 }}>
            <button
              onClick={() => setPeriodOpen(!periodOpen)}
              className="flex w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 transition-colors hover:border-slate-300"
            >
              <span>{selectedPeriod.label}</span>
              <ChevronDownIcon size={14} className="text-slate-400" />
            </button>
            {periodOpen && (
              <div className="absolute top-11 right-0 z-10 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                {PERIOD_OPTIONS.map((opt) => (
                  <button
                    key={opt.timeframe}
                    onClick={() => {
                      setSelectedPeriod(opt)
                      setPeriodOpen(false)
                    }}
                    className={`block w-full px-3 py-2 text-left text-sm transition-colors ${opt.timeframe === selectedPeriod.timeframe ? 'bg-slate-50 font-medium text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="rounded-2xl border border-slate-200/60 bg-white p-8 shadow-sm">
          <div className="flex items-center justify-center gap-3 text-slate-400">
            <Loader2Icon className="h-5 w-5 animate-spin" />
            <span className="text-sm">Carregando métricas...</span>
          </div>
        </div>
      )}

      {!isLoading && graphs.length === 0 && (
        <div className="rounded-2xl border border-slate-200/60 bg-white p-8 text-center shadow-sm">
          <MonitorIcon className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm text-slate-500">
            Nenhum dado de métrica disponível para este período.
          </p>
          <p className="mt-1 text-xs text-slate-400">
            A VM precisa estar em execução para gerar métricas.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {graphs.map(({ title, series, yLabel }) => (
          <div
            key={title}
            className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm"
          >
            <h4 className="mb-3 text-xs font-semibold tracking-wider text-slate-400 uppercase">
              {title}
            </h4>
            <MiniAreaChart series={series} height={220} yLabel={yLabel} xLabels={xLabels} />
            <div className="mt-3 flex justify-center gap-5">
              {series.map((s) => (
                <span key={s.label} className="flex items-center gap-1.5 text-xs text-slate-500">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: s.color }}
                  />
                  {s.label}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

function AccessSection({
  instance,
  refetch,
}: {
  instance: VMInstanceDetails
  refetch: () => void
}) {
  const reachableIp = resolveReachableIp(instance)
  const [consoleType, setConsoleType] = useState<'vnc' | 'serial'>('vnc')
  const [consoleOpen, setConsoleOpen] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [consoleError, setConsoleError] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [rdpDownloadLoading, setRdpDownloadLoading] = useState(false)
  const consoleContainerRef = useRef<HTMLDivElement>(null)
  const vncConsoleRef = useRef<any>(null)
  // DUAS réguas, de propósito — ver `shared/utils/vmOsFamily.ts` (#729).
  //
  // A de apresentação (RDP vs SSH) aceita o `ostype` do PVE quando o backend não sabe a
  // família: errar ali só dá uma tela inconveniente, e a pista costuma acertar.
  //
  // A das credenciais NÃO aceita: ela descreve o que o SERVIDOR vai fazer, e lá o
  // indeterminado é tratado como Linux. Uma VM Windows legada (sem template, sem pista do
  // guest) chega com `os_family: null`; se o formulário caísse no PVE, anunciaria "a senha
  // é aplicada pelo agente convidado, sem reiniciar a VM" numa VM que o backend REINICIA
  // quando o guest agent não responde — a tela prometendo o oposto do que acontece, que é
  // o defeito desta issue mudando de lugar.
  const isWindowsInstance = ehWindowsParaApresentacao(instance)
  const isWindowsCredenciais = ehWindowsParaCredenciais(instance)

  const { data: rdpgwStatus } = useQuery({
    queryKey: ['rdpgw-status'],
    queryFn: () => getRDPGatewayStatus(),
    enabled: isWindowsInstance,
  })

  const rdpgwReady = rdpgwStatus?.rdpgw_provision_status === 'active'

  const resetMutation = useMutation({
    mutationFn: (data: { new_password: string; username?: string }) =>
      instancesApi.resetCredentials(instance.id, data),
    onSuccess: (data) => {
      // A mensagem vem do backend porque só ele sabe qual caminho será tentado: no
      // Windows a senha entra pelo guest agent e a VM NÃO reinicia, então o antigo
      // "a VM está reiniciando" fixo anunciava um downtime que não acontece (#729).
      toast.success(data?.message || 'Credenciais redefinidas!')
      refetch()
    },
    // `getApiErrorText` e não `data.detail`: o handler de `HTTPException` do backend
    // devolve `{ error: { message } }`, então ler `detail` engolia a frase do servidor e
    // mostrava o genérico. É o que aconteceria com a recusa de redefinir senha em VM
    // Windows desligada — a única mensagem que diz ao cliente o que fazer (#729).
    onError: (err: any) => toast.error(getApiErrorText(err) || 'Erro ao redefinir credenciais'),
  })

  // Fetch console info to check availability
  const { data: consoleInfo } = useQuery({
    queryKey: ['console-info', instance.id],
    queryFn: () => consoleApi.getConsoleInfo(instance.id),
    enabled: consoleOpen,
  })

  // Auto-select serial when default protocol is serial
  useState(() => {
    if (consoleInfo?.default_protocol === 'serial') setConsoleType('serial')
  })

  // O serial pode deixar de ser anunciado entre um poll e outro (#1078): volta ao VNC em
  // vez de seguir pedindo ticket de um console que o `console/info` acabou de negar.
  useEffect(() => {
    if (consoleType === 'serial' && consoleInfo && !consoleInfo.serial_available) {
      setConsoleType('vnc')
    }
  }, [consoleType, consoleInfo])

  // Fetch ticket when console is open
  const { data: consoleTicket, isLoading: ticketLoading } = useQuery({
    queryKey: ['console-ticket', instance.id, consoleType],
    queryFn: () => consoleApi.getConsoleTicket(instance.id, consoleType),
    enabled: consoleOpen && !!consoleInfo?.console_available,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  const toggleFullscreen = () => {
    if (!consoleContainerRef.current) return
    if (!isFullscreen) {
      consoleContainerRef.current.requestFullscreen?.()
      setIsFullscreen(true)
    } else {
      // exitFullscreen returns a Promise that rejects when the document is not
      // active (e.g. tab switch); the rejection is async, so try/catch can't
      // see it — handle via .catch().
      document.exitFullscreen?.().catch(() => {})
      setIsFullscreen(false)
    }
  }

  const handleDownloadRdp = async () => {
    try {
      setRdpDownloadLoading(true)
      const { blob, filename } = await consoleApi.downloadRdpFile(instance.id)
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(blobUrl)
      toast.success('Arquivo .rdp gerado com sucesso')
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          'Erro ao gerar arquivo .rdp',
      )
    } finally {
      setRdpDownloadLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Inline Console */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/60 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <div className="flex items-center gap-3">
            <h4 className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
              Console
            </h4>
            {consoleOpen && (
              <div className="flex items-center gap-1.5">
                <div
                  className={`h-2 w-2 rounded-full ${isConnected ? 'bg-emerald-500' : consoleError ? 'bg-red-500' : 'animate-pulse bg-amber-500'}`}
                />
                <span className="text-xs text-slate-500">
                  {isConnected ? 'Conectado' : consoleError ? 'Erro' : 'Conectando...'}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isWindowsInstance && (
              <button
                onClick={() => {
                  if (!rdpgwReady) {
                    toast.error(
                      'O acesso RDP requer um Cloud Gateway configurado. Configure o gateway na seção Networking.',
                    )
                    return
                  }
                  handleDownloadRdp()
                }}
                disabled={rdpDownloadLoading || instance.status !== 'running'}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${rdpgwReady ? 'bg-sky-50 text-sky-700 hover:bg-sky-100' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`}
                title={
                  !rdpgwReady
                    ? 'Requer Cloud Gateway configurado'
                    : 'Baixar arquivo .rdp para acesso Windows'
                }
              >
                {rdpDownloadLoading ? (
                  <Loader2Icon className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <DownloadIcon className="h-3.5 w-3.5" />
                )}
                Baixar RDP
              </button>
            )}
            {consoleOpen && (
              <>
                <div className="flex overflow-hidden rounded-lg border border-slate-200">
                  <button
                    onClick={() => setConsoleType('vnc')}
                    className={`px-3 py-1 text-xs font-medium transition-colors ${consoleType === 'vnc' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                  >
                    VNC
                  </button>
                  {/* Serial só quando o `console/info` o anuncia: o backend o tira quando o
                      agent do node não abre o serial ou a VM não tem `serial0: socket` (#1078). */}
                  {!isWindowsInstance && consoleInfo?.serial_available && (
                    <button
                      onClick={() => setConsoleType('serial')}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${consoleType === 'serial' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                    >
                      Serial
                    </button>
                  )}
                </div>
                <button
                  onClick={toggleFullscreen}
                  className="p-1.5 text-slate-400 transition-colors hover:text-slate-600"
                  title="Tela cheia"
                >
                  {isFullscreen ? (
                    <Minimize2Icon className="h-4 w-4" />
                  ) : (
                    <Maximize2Icon className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={() => {
                    const w = 1280,
                      h = 800,
                      l = (window.screen.width - w) / 2,
                      t = (window.screen.height - h) / 2
                    window.open(
                      `/instances/${instance.id}/console/popup`,
                      'vnc-console',
                      `width=${w},height=${h},left=${l},top=${t},resizable=yes`,
                    )
                  }}
                  className="p-1.5 text-slate-400 transition-colors hover:text-slate-600"
                  title="Abrir em popup"
                >
                  <MonitorIcon className="h-4 w-4" />
                </button>
              </>
            )}
            <button
              onClick={() => {
                setConsoleOpen(!consoleOpen)
                setConsoleError(null)
              }}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${consoleOpen ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
            >
              <TerminalIcon className="h-3.5 w-3.5" />
              {consoleOpen ? 'Fechar' : 'Abrir Console'}
            </button>
          </div>
        </div>

        {consoleOpen && (
          <div ref={consoleContainerRef} className={`bg-black ${isFullscreen ? '' : 'h-[500px]'}`}>
            {isWindowsInstance && (
              <div className="border-b border-sky-900 bg-sky-950/95 px-4 py-2 text-xs text-sky-100">
                Para acesso Windows completo, prefira o arquivo{' '}
                <code className="font-mono">.rdp</code>.
              </div>
            )}
            {ticketLoading && (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <Loader2Icon className="mx-auto mb-3 h-8 w-8 animate-spin text-slate-400" />
                  <p className="text-sm text-slate-400">Obtendo ticket de acesso...</p>
                </div>
              </div>
            )}
            {consoleError && (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <AlertCircleIcon className="mx-auto mb-3 h-8 w-8 text-red-400" />
                  <p className="mb-3 text-sm text-red-400">{consoleError}</p>
                  <button
                    onClick={() => {
                      setConsoleError(null)
                      setConsoleOpen(false)
                      setTimeout(() => setConsoleOpen(true), 100)
                    }}
                    className="rounded-lg bg-slate-700 px-4 py-2 text-xs font-medium text-white transition-colors hover:bg-slate-600"
                  >
                    Reconectar
                  </button>
                </div>
              </div>
            )}
            {consoleTicket?.websocket_url &&
              !consoleError &&
              !ticketLoading &&
              (consoleType === 'serial' ? (
                <SerialConsole
                  ref={vncConsoleRef}
                  websocketUrl={consoleTicket.websocket_url}
                  instanceId={instance.id}
                  onConnect={() => {
                    setIsConnected(true)
                    setConsoleError(null)
                  }}
                  onDisconnect={() => setIsConnected(false)}
                  onError={(err) => {
                    setConsoleError(err.message)
                    setIsConnected(false)
                  }}
                  className="h-full w-full"
                />
              ) : (
                <VNCConsole
                  ref={vncConsoleRef}
                  websocketUrl={consoleTicket.websocket_url}
                  instanceId={instance.id}
                  vncTicket={consoleTicket.ticket || consoleTicket.password}
                  onConnect={() => {
                    setIsConnected(true)
                    setConsoleError(null)
                  }}
                  onDisconnect={() => setIsConnected(false)}
                  onError={(err) => {
                    setConsoleError(err.message)
                    setIsConnected(false)
                  }}
                  className="h-full w-full"
                />
              ))}
            {!consoleTicket?.websocket_url &&
              !ticketLoading &&
              !consoleError &&
              consoleInfo &&
              !consoleInfo.console_available && (
                <div className="flex h-full items-center justify-center">
                  <div className="text-center">
                    <AlertCircleIcon className="mx-auto mb-3 h-8 w-8 text-amber-400" />
                    <p className="text-sm text-amber-400">
                      Console não disponível. Verifique se a VM está em execução.
                    </p>
                  </div>
                </div>
              )}
          </div>
        )}
      </div>

      {/* Remote access info */}
      {reachableIp && !isWindowsInstance && (
        <div className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm">
          <h4 className="mb-4 text-xs font-semibold tracking-wider text-slate-400 uppercase">
            SSH
          </h4>
          <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
            <code className="flex-1 font-mono text-sm text-slate-700">
              ssh {instance.initial_user || 'root'}@{reachableIp}
            </code>
            <CopyButton text={`ssh ${instance.initial_user || 'root'}@${reachableIp}`} />
          </div>
        </div>
      )}
      {reachableIp && isWindowsInstance && (
        <div className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm">
          <h4 className="mb-4 text-xs font-semibold tracking-wider text-slate-400 uppercase">
            Área de Trabalho Remota (RDP)
          </h4>
          <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
            <code className="flex-1 font-mono text-sm text-slate-700">
              {reachableIp}:{3389}
            </code>
            <CopyButton text={reachableIp} />
          </div>
          <p className="mt-2 text-xs text-slate-400">
            Use o cliente de Área de Trabalho Remota (mstsc) ou baixe o arquivo .rdp acima.
          </p>
        </div>
      )}

      {/* Credenciais — o que aparece aqui é decidido pelo BACKEND
          (`supports_cloudinit_credentials`), não por um `!isWindows` no JSX. Ver
          `InstanceCredentialsCard`. */}
      <InstanceCredentialsCard
        instance={instance}
        isWindows={isWindowsCredenciais}
        /* `mutateAsync`: o card fecha o formulário no resolve, e mantém o que foi
           digitado quando a API recusa (VM Windows desligada, por exemplo). */
        onReset={(data) => resetMutation.mutateAsync(data)}
        resetPending={resetMutation.isPending}
      />
    </div>
  )
}

function PowerSection({ instance, refetch }: { instance: VMInstanceDetails; refetch: () => void }) {
  const isRunning = instance.proxmox_details?.status === 'running' || instance.status === 'running'

  const actionMutation = useMutation({
    mutationFn: async (action: string) => {
      const response = await apiClient.post(
        `/api/v1/end-customer/instances/${instance.id}/action`,
        { action },
      )
      return response
    },
    onSuccess: (data: any) => {
      refetch()
      toast.success(data.message || 'Ação executada')
    },
    onError: () => toast.error('Erro ao executar ação'),
  })

  const actions = [
    {
      action: 'start',
      label: 'Iniciar',
      icon: PlayIcon,
      desc: 'Liga a instância',
      color: 'bg-emerald-600 hover:bg-emerald-700',
      disabled: isRunning,
    },
    {
      action: 'stop',
      label: 'Desligar',
      icon: SquareIcon,
      desc: 'Desliga a instância de forma segura (ACPI shutdown)',
      color: 'bg-red-600 hover:bg-red-700',
      disabled: !isRunning,
    },
    {
      action: 'restart',
      label: 'Reiniciar',
      icon: RotateCcwIcon,
      desc: 'Reinicia a instância (ACPI reboot)',
      color: 'bg-amber-600 hover:bg-amber-700',
      disabled: !isRunning,
    },
  ]

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm">
        <h4 className="mb-4 text-xs font-semibold tracking-wider text-slate-400 uppercase">
          Controle de Power
        </h4>
        <div className="space-y-3">
          {actions.map(({ action, label, icon: Icon, desc, color, disabled }) => (
            <div
              key={action}
              className="flex items-center justify-between rounded-xl border border-slate-100 p-4 transition-colors hover:border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50">
                  <Icon className="h-5 w-5 text-slate-500" />
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-900">{label}</div>
                  <div className="text-xs text-slate-400">{desc}</div>
                </div>
              </div>
              <button
                onClick={() => actionMutation.mutate(action)}
                disabled={disabled || actionMutation.isPending}
                className={`flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${color}`}
              >
                {actionMutation.isPending ? (
                  <Loader2Icon className="h-3 w-3 animate-spin" />
                ) : (
                  <Icon className="h-3 w-3" />
                )}
                {label}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm">
        <h4 className="mb-3 text-xs font-semibold tracking-wider text-slate-400 uppercase">
          Status Atual
        </h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-slate-400">Estado:</span>
            <span
              className={`ml-2 font-medium ${isRunning ? 'text-emerald-600' : 'text-slate-600'}`}
            >
              {isRunning ? 'Executando' : 'Parada'}
            </span>
          </div>
          {instance.proxmox_details?.uptime != null && (
            <div>
              <span className="text-slate-400">Uptime:</span>
              <span className="ml-2 font-medium text-slate-700">
                {formatUptime(instance.proxmox_details.uptime)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Aba "Recursos" (issue #759).
 *
 * Três blocos para três conceitos que antes eram um número só: computação, o disco de
 * SISTEMA e os discos adicionais. O redimensionamento deixou de ser a identidade da aba
 * e virou a ação de cada bloco.
 *
 * O disco de sistema sai de `system_disk_gb` — nunca de `disk_gb`, que é a soma com os
 * block storages anexados. Era essa soma que servia de piso ao resize: numa VM de 40 GB
 * com um volume de 100, a tela dizia 140 e um pedido de "+10 GB" crescia o disco de
 * sistema para 150.
 */
function ResourcesSection({
  instance,
  refetch,
}: {
  instance: VMInstanceDetails
  refetch: () => void
}) {
  const px = instance.proxmox_details
  const currentCores = px?.cpu_cores || 1
  const currentMemoryGb = px?.memory_gb || 1
  // `?? null` e não `|| 0`: zero e ausente são coisas diferentes aqui, e um piso 0
  // reabriria o buraco por outro caminho.
  // `Math.ceil`: o backend devolve o tamanho com uma casa (um disco de `size=512M`
  // vira 0.5) e `disk_gb` do endpoint é `int` — mandar 40.5 dava 422 antes de
  // qualquer lógica, com toast genérico. Arredondar para CIMA mantém o piso válido
  // (nunca pede menos do que o disco já tem).
  const systemDiskGb = px?.system_disk_gb != null ? Math.ceil(px.system_disk_gb) : null

  // Só `per_resource` pode ser redimensionado: o backend recusa com 409
  // `resize_indisponivel` o produto `fixed` (#1150). Produto não carregado ou com modelo
  // desconhecido também NÃO oferece: a tela é mais restritiva que o backend nesse caso
  // (falha fechada), e o 409 de uma corrida continua chegando ao toast.
  const podeRedimensionar = (instance as any).product?.pricing_model === 'per_resource'

  const [editing, setEditing] = useState<null | 'compute' | 'disk'>(null)
  const [cores, setCores] = useState(currentCores)
  const [memoryGb, setMemoryGb] = useState(currentMemoryGb)
  const [diskGb, setDiskGb] = useState(systemDiskGb ?? 0)

  const maxCpu = 32
  const maxMemGb = 64
  // Nunca menor que o disco atual: uma VM já acima do teto padrão deixaria o input
  // com `min` maior que `max` e o slider inutilizável.
  const maxDiskGb = Math.max(2000, (systemDiskGb ?? 0) * 2)

  const { data: volumesData, isError: volumesErro } = useQuery({
    // A key COMEÇA com 'block-storage-volumes' de propósito: as mutações da página
    // Armazenamento (anexar, desanexar, redimensionar, criar, excluir) invalidam
    // `['block-storage-volumes']`, e o React Query casa por PREFIXO — então esta
    // lista se invalida junto, sem precisar tocar naqueles cinco call sites. Com a
    // key própria de antes, anexar um volume lá deixava esta aba mostrando o estado
    // velho até o cache expirar (5 min) ou a janela reganhar foco.
    queryKey: ['block-storage-volumes', 'by-vm', instance.id],
    // O filtro é do backend: peneirar no navegador erraria assim que os volumes da VM
    // caíssem fora da primeira página do cliente.
    queryFn: () => blockStorageApi.listVolumes({ vm_instance_id: instance.id, limit: 100 }),
  })
  const volumes = volumesData?.volumes ?? []

  const abrirComputacao = () => {
    setCores(currentCores)
    setMemoryGb(currentMemoryGb)
    setEditing('compute')
  }
  const abrirDisco = () => {
    setDiskGb(systemDiskGb ?? 0)
    setEditing('disk')
  }

  const computeChanged = cores !== currentCores || memoryGb !== currentMemoryGb
  const diskChanged = systemDiskGb != null && diskGb > systemDiskGb

  const calcPrice = useMemo(() => {
    const product = (instance as any).product
    if (!product || product.pricing_model !== 'per_resource') return null
    const markup = (instance as any).markup_percentage
      ? 1 + (instance as any).markup_percentage / 100
      : 1
    return (cpu: number, mem: number, disk: number) =>
      ((product.base_price || 0) +
        (product.price_per_cpu || 0) * cpu +
        (product.price_per_memory_gb || 0) * mem +
        (product.price_per_storage_gb || 0) * disk) *
      markup
  }, [instance])

  /**
   * O disco que entra na conta é o de SISTEMA: o block storage anexado já é cobrado
   * como produto próprio, e somá-lo aqui inflava a projeção do resize.
   */
  const priceEstimate = useMemo(() => {
    if (!calcPrice) return null
    // Sem disco de sistema identificado, `systemDiskGb ?? 0` tiraria TODO o custo de
    // armazenamento da conta e mostraria um mensal menor do que o real — enquanto o
    // backend segue precificando com o `storage_gb` do banco. Melhor não estimar do
    // que estimar para menos: o número da tela vira expectativa de fatura.
    if (systemDiskGb == null) return null
    const diskAtual = systemDiskGb ?? 0
    const diskNovo = editing === 'disk' ? diskGb : diskAtual
    const cpuNovo = editing === 'compute' ? cores : currentCores
    const memNovo = editing === 'compute' ? memoryGb : currentMemoryGb
    const currentPrice = calcPrice(currentCores, currentMemoryGb, diskAtual)
    const newPrice = calcPrice(cpuNovo, memNovo, diskNovo)
    return { currentPrice, newPrice, diff: newPrice - currentPrice }
  }, [calcPrice, editing, cores, memoryGb, diskGb, currentCores, currentMemoryGb, systemDiskGb])

  const updateMutation = useMutation({
    mutationFn: (data: { cores?: number; memory?: number; disk_gb?: number }) =>
      instancesApi.updateInstance(instance.id, data),
    onSuccess: () => {
      toast.success('Configuração atualizada! A VM precisa ser reiniciada.')
      setEditing(null)
      refetch()
    },
    // `detail` não existe na resposta: o handler global reembrulha em
    // `{error:{message}}`. Lendo o campo errado, recusas úteis do backend
    // ("Crédito insuficiente para o upgrade", "Disco só pode ser aumentado")
    // chegavam ao cliente como "Erro ao atualizar".
    onError: (err: any) => {
      toast.error(getApiErrorText(err) || 'Erro ao atualizar')
      // Erro NÃO significa "nada mudou": no sucesso parcial (disco cresceu, CPU
      // falhou) o backend persiste e cobra o disco e ainda assim responde 500. Sem
      // este refetch a tela seguia mostrando o disco antigo, e a nova tentativa
      // naquele valor virava no-op (409 do `skipped`) sem o cliente entender por quê.
      refetch()
    },
  })

  const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v))

  const precoEstimado = priceEstimate && (
    <div
      data-testid="preco-estimado"
      className="rounded-xl border border-slate-200/60 bg-slate-50 p-4"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="mb-0.5 text-xs text-slate-400">Preço mensal estimado</p>
          <p className="text-lg font-bold text-slate-900">
            R$ {priceEstimate.newPrice.toFixed(2)}
            <span className="text-xs font-normal text-slate-400">/mês</span>
          </p>
        </div>
        {priceEstimate.diff !== 0 && (
          <div
            className={`text-right ${priceEstimate.diff > 0 ? 'text-amber-600' : 'text-green-600'}`}
          >
            <p className="text-xs opacity-75">{priceEstimate.diff > 0 ? 'Aumento' : 'Redução'}</p>
            <p className="text-sm font-semibold">
              {priceEstimate.diff > 0 ? '+' : ''}R$ {priceEstimate.diff.toFixed(2)}
            </p>
          </div>
        )}
      </div>
      {priceEstimate.diff > 0 && (
        <p className="mt-2 border-t border-slate-200/60 pt-2 text-[11px] text-slate-400">
          Pro-rata proporcional aos dias restantes do ciclo será cobrado imediatamente.
        </p>
      )}
    </div>
  )

  const avisos = (
    <div className="space-y-2">
      {editing === 'compute' && (
        <div className="flex items-start gap-2 rounded-2xl border border-amber-200/60 bg-amber-50 p-4">
          <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
          <p className="text-xs text-amber-700">
            A VM precisa estar parada para aplicar alterações de CPU/memória, ou será reiniciada
            automaticamente.
          </p>
        </div>
      )}
      <div className="flex items-start gap-2 rounded-2xl border border-blue-200/60 bg-blue-50 p-4">
        <InfoIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
        <p className="text-xs text-blue-700">
          Alterações de recursos impactam o valor da fatura. Upgrades geram cobrança pro-rata
          imediata do período restante.
        </p>
      </div>
    </div>
  )

  return (
    <div className="space-y-4">
      {/* ── Computação ────────────────────────────────────── */}
      <div
        data-testid="computacao"
        className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm"
      >
        <div className="mb-6 flex items-center justify-between">
          <h4 className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
            Computação
          </h4>
          {editing !== 'compute' && podeRedimensionar && (
            <button
              onClick={abrirComputacao}
              disabled={editing === 'disk'}
              className="text-xs font-medium text-slate-500 transition-colors hover:text-slate-700 disabled:opacity-40"
            >
              Redimensionar
            </button>
          )}
        </div>

        {!podeRedimensionar && editing === null && (
          <p data-testid="recursos-fixos-computacao" className="mb-4 text-xs text-slate-400">
            A configuração desta VM é fixa pelo plano contratado.
          </p>
        )}

        {editing === 'compute' ? (
          <div className="space-y-8">
            {/* CPU */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
                    <CpuIcon className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">CPU</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={maxCpu}
                    value={cores}
                    aria-label="vCPUs"
                    onChange={(e) => setCores(clamp(Number(e.target.value), 1, maxCpu))}
                    className="w-20 rounded-lg border border-slate-200 px-2 py-1.5 text-center text-sm focus:border-blue-300 focus:ring-2 focus:ring-blue-500/20"
                  />
                  <span className="text-xs text-slate-400">vCPUs</span>
                </div>
              </div>
              <input
                type="range"
                min={1}
                max={maxCpu}
                step={1}
                value={cores}
                aria-label="CPU"
                onChange={(e) => setCores(Number(e.target.value))}
                className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
              />
              <div className="mt-1 flex justify-between">
                <span className="text-[10px] text-slate-400">1 vCPU</span>
                <span className="text-[10px] text-slate-400">{maxCpu} vCPUs</span>
              </div>
            </div>

            {/* Memória */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-50">
                    <DatabaseIcon className="h-4 w-4 text-violet-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">Memória</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={maxMemGb}
                    value={memoryGb}
                    aria-label="Memória em GB"
                    onChange={(e) => setMemoryGb(clamp(Number(e.target.value), 1, maxMemGb))}
                    className="w-20 rounded-lg border border-slate-200 px-2 py-1.5 text-center text-sm focus:border-violet-300 focus:ring-2 focus:ring-violet-500/20"
                  />
                  <span className="text-xs text-slate-400">GB</span>
                </div>
              </div>
              <input
                type="range"
                min={1}
                max={maxMemGb}
                step={1}
                value={memoryGb}
                aria-label="Memória"
                onChange={(e) => setMemoryGb(Number(e.target.value))}
                className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-violet-600"
              />
              <div className="mt-1 flex justify-between">
                <span className="text-[10px] text-slate-400">1 GB</span>
                <span className="text-[10px] text-slate-400">{maxMemGb} GB</span>
              </div>
            </div>

            {precoEstimado}

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
              <button
                onClick={() => {
                  setEditing(null)
                  setCores(currentCores)
                  setMemoryGb(currentMemoryGb)
                }}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => updateMutation.mutate({ cores, memory: memoryGb * 1024 })}
                disabled={updateMutation.isPending || !computeChanged}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800 disabled:opacity-50"
              >
                {updateMutation.isPending ? (
                  <>
                    <Loader2Icon className="h-4 w-4 animate-spin" /> Salvando...
                  </>
                ) : (
                  'Aplicar Alterações'
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50">
                <CpuIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <span className="text-lg font-semibold text-slate-900">
                  {px?.cpu_cores || '—'} vCPUs
                </span>
                {px?.cpu_usage != null && (
                  <p className="text-xs text-slate-400">{px.cpu_usage.toFixed(1)}% em uso</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50">
                <DatabaseIcon className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <span className="text-lg font-semibold text-slate-900">
                  {px?.memory_gb ? `${px.memory_gb} GB` : '—'}
                </span>
                {px?.memory_usage_percent != null && (
                  <p className="text-xs text-slate-400">
                    {px.memory_usage_percent.toFixed(1)}% em uso
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Disco do sistema ──────────────────────────────── */}
      <div
        data-testid="disco-sistema"
        className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm"
      >
        <div className="mb-6 flex items-center justify-between">
          <h4 className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
            Disco do sistema
          </h4>
          {editing !== 'disk' && systemDiskGb != null && podeRedimensionar && (
            <button
              onClick={abrirDisco}
              disabled={editing === 'compute'}
              className="text-xs font-medium text-slate-500 transition-colors hover:text-slate-700 disabled:opacity-40"
            >
              Aumentar
            </button>
          )}
        </div>

        {!podeRedimensionar && editing === null && (
          <p data-testid="recursos-fixos-disco" className="mb-4 text-xs text-slate-400">
            A configuração desta VM é fixa pelo plano contratado.
          </p>
        )}

        {editing === 'disk' && systemDiskGb != null ? (
          <div className="space-y-8">
            <div>
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50">
                    <HardDriveIcon className="h-4 w-4 text-amber-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">
                    {px?.system_disk_slot ? `${px.system_disk_slot} · ` : ''}Disco do sistema
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    data-testid="input-disco-sistema"
                    aria-label="Tamanho do disco do sistema em GB"
                    min={systemDiskGb}
                    max={maxDiskGb}
                    step={10}
                    value={diskGb}
                    onChange={(e) =>
                      setDiskGb(clamp(Number(e.target.value), systemDiskGb, maxDiskGb))
                    }
                    className="w-20 rounded-lg border border-slate-200 px-2 py-1.5 text-center text-sm focus:border-amber-300 focus:ring-2 focus:ring-amber-500/20"
                  />
                  <span className="text-xs text-slate-400">GB</span>
                </div>
              </div>
              <input
                type="range"
                min={systemDiskGb}
                max={maxDiskGb}
                step={10}
                value={diskGb}
                aria-label="Disco do sistema"
                onChange={(e) => setDiskGb(Number(e.target.value))}
                className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-amber-600"
              />
              <div className="mt-1 flex justify-between">
                <span className="text-[10px] text-slate-400">{systemDiskGb} GB (atual)</span>
                <span className="text-[10px] text-slate-400">{maxDiskGb} GB</span>
              </div>
            </div>

            {precoEstimado}

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
              <button
                onClick={() => {
                  setEditing(null)
                  setDiskGb(systemDiskGb)
                }}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => updateMutation.mutate({ disk_gb: diskGb })}
                disabled={updateMutation.isPending || !diskChanged}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800 disabled:opacity-50"
              >
                {updateMutation.isPending ? (
                  <>
                    <Loader2Icon className="h-4 w-4 animate-spin" /> Salvando...
                  </>
                ) : (
                  'Aplicar Alterações'
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50">
              <HardDriveIcon className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <span className="text-lg font-semibold text-slate-900">
                {systemDiskGb != null ? `${systemDiskGb} GB` : '—'}
              </span>
              <p className="text-xs text-slate-400">
                {systemDiskGb != null
                  ? [px?.system_disk_slot, px?.system_disk_storage, 'só pode crescer']
                      .filter(Boolean)
                      .join(' · ')
                  : px?.system_disk_issue === 'volume_without_address'
                    ? // Culpar o boot da VM aqui mandava o cliente investigar o lugar
                      // errado: a causa é um volume anexado que não foi localizado no
                      // Proxmox, e quem resolve isso é o vínculo do volume.
                      'Não foi possível localizar um dos volumes anexados a esta VM. Enquanto isso, o disco de sistema não pode ser aumentado.'
                    : px?.system_disk_issue === 'lookup_failed'
                      ? // Falha nossa, não da VM: prometer "não identificamos o disco"
                        // aqui mandaria o cliente investigar o Proxmox por um erro que
                        // está no nosso lado e que uma nova tentativa resolve.
                        'Não foi possível consultar os discos desta VM agora. Tente recarregar em instantes.'
                      : 'Não foi possível identificar o disco de sistema desta VM.'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── Discos adicionais ─────────────────────────────── */}
      {/* Falha ao carregar é dita, não escondida: lista vazia e erro produziriam a
          mesma tela (bloco ausente), e o cliente leria "esta VM não tem volume" —
          justamente a confusão que a #759 ataca. */}
      {volumesErro && (
        <div
          data-testid="discos-adicionais-erro"
          className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm"
        >
          <p className="text-xs text-slate-400">
            Não foi possível carregar os discos adicionais desta VM.
          </p>
        </div>
      )}
      {!volumesErro && volumes.length > 0 && (
        <div
          data-testid="discos-adicionais"
          className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm"
        >
          <div className="mb-4 flex items-center justify-between">
            <h4 className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
              Discos adicionais ({volumes.length})
            </h4>
            <Link
              to="/storage"
              className="text-xs font-medium text-slate-500 transition-colors hover:text-slate-700"
            >
              Gerenciar em Armazenamento
            </Link>
          </div>
          <ul className="divide-y divide-slate-100">
            {volumes.map((volume) => (
              <li key={volume.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-slate-50">
                  <DatabaseIcon className="h-4 w-4 text-slate-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900">{volume.name}</p>
                  <p className="text-xs text-slate-400">
                    {getVolumeStatusLabel(volume.status)}
                    {volume.attached_scsi_slot ? ` · ${volume.attached_scsi_slot}` : ''}
                  </p>
                </div>
                <span className="flex-shrink-0 text-sm font-semibold text-slate-700">
                  {volume.size_gb} GB
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 border-t border-slate-100 pt-3 text-[11px] text-slate-400">
            Volumes são cobrados à parte e não entram no tamanho do disco do sistema.
          </p>
        </div>
      )}

      {editing && avisos}
    </div>
  )
}

/** Quantos eventos por página — o "Carregar mais" pede outra fatia deste tamanho. */
const HISTORICO_POR_PAGINA = 20

const COR_DO_PONTO: Record<NivelDoEvento, string> = {
  info: 'bg-slate-300',
  alerta: 'bg-amber-400',
  erro: 'bg-rose-500',
}

/**
 * Aba Histórico da instância (issue #794).
 *
 * Até aqui eram quatro linhas escritas no código — iguais para toda VM, de todo cliente —
 * afirmando um snapshot que nunca existiu, um resize que nunca houve e um usuário que não
 * é da organização. Agora o conteúdo é o `audit_logs` da própria VM: sem registro, a tela
 * diz que não há registro.
 */
function HistorySection({ vmId }: { vmId: string }) {
  // Paginação por CURSOR. Duas razões, nesta ordem:
  //
  // 1. um `limit` que cresce a cada clique bateria no teto do endpoint (`le=200`) e
  //    devolveria 422 no 11º — a aba inteira viraria tela de erro justamente na VM de
  //    histórico mais longo, que é onde ela mais serve;
  // 2. offset não serve para uma lista que cresce pela ponta em que se lê: um evento
  //    gravado entre dois cliques empurra a janela e a página seguinte repete linhas. Com
  //    dedupe, a repetição ainda contaria para "carreguei tudo" e o botão sumiria com
  //    eventos de fora — pior que a linha duplicada, porque some calado.
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['instance-history', vmId],
    queryFn: ({ pageParam }) =>
      instancesApi.getInstanceHistory(vmId, { before: pageParam, limit: HISTORICO_POR_PAGINA }),
    initialPageParam: null as string | null,
    getNextPageParam: (ultima) => ultima.next_cursor ?? undefined,
  })

  // Dedupe por id: rede de segurança barata. O cursor não repete linha, mas um refetch
  // parcial ou uma página servida por uma versão antiga do backend repetiria — e chave
  // React duplicada quebra a lista inteira.
  const entradas = Array.from(
    new Map((data?.pages.flatMap((pagina) => pagina.history) ?? []).map((e) => [e.id, e])).values(),
  )
  // O total vem da página mais recente: entre um clique e outro a VM pode ter registrado
  // eventos, e o número ao lado do botão tem de acompanhar.
  const total = data?.pages[data.pages.length - 1]?.total ?? 0
  const temMais = hasNextPage

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm">
        <h4 className="mb-4 text-xs font-semibold tracking-wider text-slate-400 uppercase">
          Histórico de Atividades
        </h4>
        <div className="flex items-center gap-2 py-6 text-sm text-slate-400">
          <Loader2Icon className="h-4 w-4 animate-spin" /> Carregando histórico...
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm">
        <h4 className="mb-4 text-xs font-semibold tracking-wider text-slate-400 uppercase">
          Histórico de Atividades
        </h4>
        <div className="flex items-start gap-2 rounded-2xl border border-rose-200/60 bg-rose-50 p-4">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-600" />
          <div className="min-w-0">
            {/* Dizer que falhou, e não mostrar lista vazia: "não consegui ler" não é "não
                há nada para ler" — e a segunda leitura é a que fecha um chamado errado. */}
            <p className="text-sm text-rose-700">
              {getApiErrorText(error) || 'Não foi possível carregar o histórico desta instância.'}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-2 text-xs font-medium text-rose-700 underline"
            >
              Tentar de novo
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h4 className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
          Histórico de Atividades
        </h4>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 disabled:opacity-50"
        >
          <RefreshCwIcon className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} /> Atualizar
        </button>
      </div>

      {entradas.length === 0 ? (
        <div className="py-6 text-center">
          <ClockIcon className="mx-auto mb-2 h-5 w-5 text-slate-300" />
          <p className="text-sm text-slate-500">Ainda não há registros para esta instância.</p>
          <p className="mt-1 text-xs text-slate-400">
            As ações feitas por aqui — ligar, desligar, redimensionar, snapshots, rede e firewall —
            passam a aparecer nesta lista.
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-0">
            {entradas.map((entrada, i) => {
              const { titulo, detalhe, nivel } = descreveEvento(entrada)
              const quando = parseApiDate(entrada.created_at)
              return (
                <div
                  key={entrada.id}
                  data-testid="history-entry"
                  className={`flex items-start gap-4 py-3 ${i < entradas.length - 1 ? 'border-b border-slate-50' : ''}`}
                >
                  <div
                    className={`mt-2 h-2 w-2 flex-shrink-0 rounded-full ${COR_DO_PONTO[nivel]}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium text-slate-900">{titulo}</span>
                      <span className="text-slate-300">—</span>
                      <span className="break-all text-slate-400">{autorDoEvento(entrada)}</span>
                    </div>
                    {detalhe && (
                      <p className="mt-0.5 text-xs break-words text-slate-400">{detalhe}</p>
                    )}
                  </div>
                  {/* Relativo para leitura rápida, absoluto no `title` para a apuração: numa
                      contestação, "2 dias atrás" não serve de data. */}
                  <span
                    className="flex-shrink-0 text-xs text-slate-400"
                    title={quando.toLocaleString('pt-BR')}
                  >
                    {formatRelativeTime(entrada.created_at)}
                  </span>
                </div>
              )
            })}
          </div>

          {temMais && (
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="mt-4 w-full rounded-xl border border-slate-200 py-2 text-xs font-medium text-slate-500 hover:text-slate-700 disabled:opacity-50"
            >
              {isFetchingNextPage
                ? 'Carregando...'
                : `Carregar mais (${entradas.length} de ${total})`}
            </button>
          )}
        </>
      )}
    </div>
  )
}

function BackupsSection({ vmId }: { vmId: string }) {
  const [statusFilter, setStatusFilter] = useState('')
  const [selectedSnapshot, setSelectedSnapshot] = useState<BackupSnapshot | null>(null)
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false)

  const {
    data: snapshots = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['backup-snapshots', vmId, statusFilter],
    queryFn: () =>
      backupsApi.listSnapshots({
        vm_id: vmId,
        status: statusFilter || undefined,
        limit: 50,
      }),
  })

  const verifyMutation = useMutation({
    mutationFn: (snapshotId: string) => backupsApi.verifySnapshot(snapshotId),
    onSuccess: () => {
      toast.success('Verificação iniciada')
      refetch()
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Erro ao verificar backup'),
  })

  const restoreMutation = useMutation({
    mutationFn: (snapshotId: string) =>
      backupsApi.initiateRestore({
        snapshot_id: snapshotId,
        restore_type: 'full',
        replace_existing: true,
        start_after_restore: true,
        restore_network_config: true,
      }),
    onSuccess: () => {
      toast.success('Restauração iniciada! Acompanhe o progresso na página de Backups.')
      setShowRestoreConfirm(false)
      setSelectedSnapshot(null)
      refetch()
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Erro ao restaurar backup'),
  })

  const deleteMutation = useMutation({
    mutationFn: (snapshotId: string) => backupsApi.deleteSnapshot(snapshotId),
    onSuccess: () => {
      toast.success('Ponto de recuperação removido')
      refetch()
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Erro ao remover backup'),
  })

  const STATUS_DOTS: Record<string, string> = {
    completed: 'bg-emerald-500',
    verified: 'bg-teal-500',
    running: 'bg-blue-500 animate-pulse',
    pending: 'bg-amber-400 animate-pulse',
    failed: 'bg-rose-500',
    verifying: 'bg-purple-500 animate-pulse',
  }

  const STATUS_BADGES: Record<string, string> = {
    completed: 'bg-emerald-50 text-emerald-700 border border-emerald-200/60',
    verified: 'bg-teal-50 text-teal-700 border border-teal-200/60',
    running: 'bg-blue-50 text-blue-700 border border-blue-200/60',
    pending: 'bg-amber-50 text-amber-700 border border-amber-200/60',
    failed: 'bg-red-50 text-red-700 border border-red-200/60',
    verifying: 'bg-purple-50 text-purple-700 border border-purple-200/60',
  }

  const hasInProgress = snapshots.some((s) =>
    ['running', 'pending', 'verifying'].includes(s.status),
  )
  // O backup ainda ACONTECENDO justifica o refresh curto; a verificação, não.
  // Desde a #805 todo backup atravessa `verifying`, e essa janela vai do fim do
  // backup até o desfecho do PBS — até uma hora. A 5s isso seriam ~1.400
  // requisições por aba aberta (este mesmo auto-refresh já foi de 3s para 5s por
  // pool exhaustion), e o que muda no fim é só o rótulo: o backup já está pronto
  // e restaurável.
  const apenasVerificando =
    hasInProgress && !snapshots.some((s) => ['running', 'pending'].includes(s.status))

  // Auto-refresh when there are in-progress snapshots
  useQuery({
    queryKey: ['backup-snapshots-poll', vmId],
    queryFn: () => backupsApi.listSnapshots({ vm_id: vmId, limit: 50 }),
    enabled: hasInProgress,
    refetchInterval: apenasVerificando ? 60000 : 5000,
    select: () => {
      refetch()
      return null
    },
  })

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-slate-200/60 bg-white p-8 shadow-sm">
        <div className="flex items-center justify-center gap-3 text-slate-400">
          <Loader2Icon className="h-5 w-5 animate-spin" />
          <span className="text-sm">Carregando pontos de recuperação...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header + Filters */}
      <div className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h4 className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
              Pontos de Recuperação (PBS)
            </h4>
            <p className="mt-1 text-xs text-slate-400">
              {snapshots.length} backup{snapshots.length !== 1 ? 's' : ''} encontrado
              {snapshots.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 transition-colors hover:text-slate-700"
          >
            <RefreshCwIcon className="h-3.5 w-3.5" /> Atualizar
          </button>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600 hover:border-slate-300 focus:border-slate-300 focus:ring-2 focus:ring-slate-900/10"
          >
            <option value="">Todos os status</option>
            {/* Os TRÊS estados em que o backup existe, como na BackupsPage: o
                cliente que escolhe "Concluído" quer os backups bons, e depois da
                #805 eles terminam em `verified`. As opções abaixo continuam
                oferecendo o recorte fino. */}
            <option value={FILTRO_DE_BACKUP_CONCLUIDO}>Concluído</option>
            <option value="verifying">Verificando</option>
            <option value="verified">Verificado</option>
            <option value="running">Em execução</option>
            <option value="pending">Pendente</option>
            <option value="failed">Falhou</option>
          </select>
        </div>
      </div>

      {/* Empty state */}
      {snapshots.length === 0 && (
        <div className="rounded-2xl border border-slate-200/60 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-50">
            <ShieldIcon className="h-7 w-7 text-slate-300" />
          </div>
          <h4 className="mb-1 text-sm font-medium text-slate-700">Nenhum ponto de recuperação</h4>
          <p className="text-xs text-slate-400">
            Esta instância ainda não possui backups no PBS. Configure um job de backup na página de
            Backups.
          </p>
        </div>
      )}

      {/* Snapshot list */}
      {snapshots.map((snap) => (
        <div
          key={snap.id}
          className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm transition-colors hover:border-slate-300/60"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <div
                className={`mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ${STATUS_DOTS[snap.status] || 'bg-slate-300'}`}
              />
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-slate-900">
                    {snap.name || snap.backup_id}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium ${STATUS_BADGES[snap.status] || 'bg-slate-100 text-slate-600'}`}
                  >
                    {getBackupStatusLabel(snap.status)}
                  </span>
                  {snap.is_encrypted && (
                    <span className="inline-flex items-center rounded-md border border-slate-200/60 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
                      <KeyIcon className="mr-0.5 h-2.5 w-2.5" /> Criptografado
                    </span>
                  )}
                  {snap.protected && (
                    <span className="inline-flex items-center rounded-md border border-amber-200/60 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">
                      <ShieldIcon className="mr-0.5 h-2.5 w-2.5" /> Protegido
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
                  <span className="inline-flex items-center gap-1">
                    <ClockIcon className="h-3 w-3" />
                    {new Date(snap.backup_time).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  {snap.size_bytes != null && (
                    <span className="inline-flex items-center gap-1">
                      <HardDriveIcon className="h-3 w-3" /> {formatBackupBytes(snap.size_bytes)}
                    </span>
                  )}
                  {snap.duration_seconds != null && (
                    <span className="inline-flex items-center gap-1">
                      Duração: {formatBackupDuration(snap.duration_seconds)}
                    </span>
                  )}
                  {snap.compression_ratio != null && snap.compression_ratio > 0 && (
                    <span>Compressão: {snap.compression_ratio.toFixed(1)}x</span>
                  )}
                  {snap.is_verified && snap.verified_at && (
                    <span className="inline-flex items-center gap-1 text-teal-600">
                      <CheckCircleIcon className="h-3 w-3" />
                      Verificado em {new Date(snap.verified_at).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                </div>

                {snap.description && (
                  <p className="mt-1 truncate text-xs text-slate-400">{snap.description}</p>
                )}

                {snap.error_message && (
                  <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-red-100 bg-red-50 p-2">
                    <AlertCircleIcon className="mt-0.5 h-3 w-3 flex-shrink-0 text-red-500" />
                    <p className="text-xs text-red-600">{snap.error_message}</p>
                  </div>
                )}

                {/* Progress bar for running/pending */}
                {/* `verifying` fica de fora: o backup terminou e o
                    `progress_percentage` já está em 100, então a barra apareceria
                    cheia com "Verificando..." desde o primeiro segundo, sugerindo
                    conclusão iminente durante a verificação inteira. O estado já
                    aparece no badge. */}
                {['running', 'pending'].includes(snap.status) && snap.progress_percentage > 0 && (
                  <div className="mt-2">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-[10px] text-slate-400">Em progresso...</span>
                      <span className="text-[10px] font-medium text-slate-500">
                        {snap.progress_percentage}%
                      </span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-blue-500 transition-all duration-500"
                        style={{ width: `${snap.progress_percentage}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-shrink-0 items-center gap-1">
              {snap.status === 'completed' && (
                <button
                  onClick={() => verifyMutation.mutate(snap.id)}
                  disabled={verifyMutation.isPending}
                  title="Verificar integridade"
                  className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-600 disabled:opacity-40"
                >
                  <ShieldIcon className="h-4 w-4" />
                </button>
              )}
              {isBackupConcluido(snap.status) && (
                <button
                  onClick={() => {
                    setSelectedSnapshot(snap)
                    setShowRestoreConfirm(true)
                  }}
                  title="Restaurar"
                  className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-emerald-50 hover:text-emerald-600"
                >
                  <RotateCcwIcon className="h-4 w-4" />
                </button>
              )}
              {/* `verifying` não impede remover: o backup existe e o backend
                  aceita o DELETE nesse estado. Mantê-lo aqui escondia a ação por
                  até uma hora, agora que toda verificação passa por ele. */}
              {!snap.protected && !['running', 'pending'].includes(snap.status) && (
                <button
                  onClick={() => {
                    if (confirm(`Remover "${snap.name || snap.backup_id}"?`))
                      deleteMutation.mutate(snap.id)
                  }}
                  disabled={deleteMutation.isPending}
                  title="Remover"
                  className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                >
                  <Trash2Icon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Restore confirmation modal */}
      {showRestoreConfirm && selectedSnapshot && (
        <ConfirmModal
          isOpen={showRestoreConfirm}
          onClose={() => {
            setShowRestoreConfirm(false)
            setSelectedSnapshot(null)
          }}
          onConfirm={() => restoreMutation.mutate(selectedSnapshot.id)}
          title="Restaurar Backup"
          message={`Deseja restaurar o ponto de recuperação "${selectedSnapshot.name || selectedSnapshot.backup_id}" de ${new Date(selectedSnapshot.backup_time).toLocaleString('pt-BR')}? A instância atual será substituída e reiniciada.`}
          confirmText={restoreMutation.isPending ? 'Restaurando...' : 'Restaurar'}
          variant="warning"
        />
      )}
    </div>
  )
}

function RebuildSection({
  instance,
  refetch,
}: {
  instance: VMInstanceDetails
  refetch: () => void
}) {
  const [confirmName, setConfirmName] = useState('')
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const isStopped = instance.proxmox_details?.status === 'stopped' || instance.status === 'stopped'
  const isRebuilding = instance.status === 'rebuilding'

  const { data: rebuildData, isLoading: templatesLoading } = useQuery({
    queryKey: ['rebuild-templates', instance.id],
    queryFn: () => instancesApi.getRebuildTemplates(instance.id),
    enabled: !isRebuilding,
  })

  const rebuildMutation = useMutation({
    mutationFn: () => instancesApi.rebuildInstance(instance.id, selectedTemplateId!),
    onSuccess: () => {
      toast.success('Rebuild iniciado. A VM será recriada com o novo template.')
      setConfirmName('')
      setSelectedTemplateId(null)
      refetch()
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Erro ao iniciar rebuild'),
  })

  const canRebuild =
    isStopped && selectedTemplateId && confirmName === instance.name && !rebuildMutation.isPending

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-amber-200/60 bg-white p-5 shadow-sm">
        <h4 className="mb-4 text-xs font-semibold tracking-wider text-amber-600 uppercase">
          Rebuild (Reinstalar)
        </h4>
        <p className="mb-4 text-sm text-slate-500">
          O rebuild <strong className="text-amber-700">destroi a VM atual</strong> e recria a partir
          de um novo template. Os dados do disco serão perdidos, mas as credenciais de acesso
          (usuário/senha) e configuração de rede serão preservadas.
        </p>

        {isRebuilding && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200/60 bg-amber-50 p-4">
            <Loader2Icon className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin text-amber-600" />
            <p className="text-xs text-amber-700">Rebuild em andamento. Aguarde a conclusão...</p>
          </div>
        )}

        {!isStopped && !isRebuilding && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200/60 bg-amber-50 p-4">
            <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <p className="text-xs text-amber-700">
              A instância precisa estar <strong>parada</strong> para fazer rebuild. Desligue-a
              primeiro na aba Power.
            </p>
          </div>
        )}

        {!isRebuilding && (
          <>
            {/* Current template info */}
            {rebuildData && (
              <div className="mb-4 rounded-xl border border-slate-200/60 bg-slate-50 p-3">
                <p className="text-xs text-slate-500">Template atual</p>
                <p className="text-sm font-medium text-slate-700">
                  {rebuildData.current_template_name || 'Desconhecido'}
                </p>
              </div>
            )}

            {/* Template selection */}
            <div className="mb-4">
              <label className="mb-2 block text-xs font-medium text-slate-600">
                Selecione o novo template
              </label>
              {templatesLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Loader2Icon className="h-4 w-4 animate-spin" /> Carregando templates...
                </div>
              ) : rebuildData?.templates && rebuildData.templates.length > 0 ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {rebuildData.templates
                    .filter((t) => !t.is_current)
                    .map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setSelectedTemplateId(t.id)}
                        disabled={!isStopped}
                        className={`rounded-xl border-2 p-3 text-left transition-all ${
                          selectedTemplateId === t.id
                            ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-200/60'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        } disabled:cursor-not-allowed disabled:opacity-50`}
                      >
                        <p className="text-sm font-medium text-slate-700">{t.name}</p>
                        <p className="mt-0.5 text-xs text-slate-400">
                          {t.os_variant || t.os_type || t.category}
                        </p>
                      </button>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400">
                  Nenhum template alternativo disponível para esta categoria.
                </p>
              )}
            </div>

            {/* Confirmation */}
            <div className="max-w-md">
              <label className="mb-1 block text-xs text-slate-500">
                Digite{' '}
                <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-amber-600">
                  {instance.name}
                </code>{' '}
                para confirmar
              </label>
              <input
                type="text"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={instance.name}
                disabled={!isStopped || !selectedTemplateId}
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-amber-300 focus:ring-2 focus:ring-amber-500/10 disabled:bg-slate-50 disabled:opacity-50"
              />
            </div>

            <div className="mt-4">
              <button
                onClick={() => rebuildMutation.mutate()}
                disabled={!canRebuild}
                className="inline-flex items-center gap-2 rounded-xl bg-amber-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {rebuildMutation.isPending ? (
                  <>
                    <Loader2Icon className="h-4 w-4 animate-spin" /> Reinstalando...
                  </>
                ) : (
                  <>
                    <RefreshCwIcon className="h-4 w-4" /> Reinstalar
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function DestroySection({ instance }: { instance: VMInstanceDetails }) {
  const navigate = useNavigate()
  const [confirmName, setConfirmName] = useState('')
  const isRunning = instance.proxmox_details?.status === 'running' || instance.status === 'running'

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.delete(`/api/v1/end-customer/instances/${instance.id}`),
    onSuccess: () => {
      toast.success('Instância destruída com sucesso')
      navigate('/instances')
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Erro ao destruir instância'),
  })

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-red-200/60 bg-white p-5 shadow-sm">
        <h4 className="mb-4 text-xs font-semibold tracking-wider text-red-500 uppercase">
          Destruir Instância
        </h4>
        <p className="mb-4 text-sm text-slate-500">
          Esta ação é <strong className="text-red-600">irreversível</strong>. A instância será
          completamente removida, incluindo todos os dados no disco.
        </p>

        {isRunning && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200/60 bg-amber-50 p-4">
            <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <p className="text-xs text-amber-700">
              A instância precisa estar <strong>parada</strong> para ser destruída. Desligue-a
              primeiro na aba Power.
            </p>
          </div>
        )}

        <div className="max-w-md">
          <label className="mb-1 block text-xs text-slate-500">
            Digite{' '}
            <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-red-600">
              {instance.name}
            </code>{' '}
            para confirmar
          </label>
          <input
            type="text"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={instance.name}
            disabled={isRunning}
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-red-300 focus:ring-2 focus:ring-red-500/10 disabled:bg-slate-50 disabled:opacity-50"
          />
        </div>

        <div className="mt-4">
          <button
            onClick={() => deleteMutation.mutate()}
            disabled={confirmName !== instance.name || isRunning || deleteMutation.isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {deleteMutation.isPending ? (
              <>
                <Loader2Icon className="h-4 w-4 animate-spin" /> Destruindo...
              </>
            ) : (
              <>
                <Trash2Icon className="h-4 w-4" /> Destruir Instância
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// Main Page
// ════════════════════════════════════════════════════════════
export default function InstanceDetailPage() {
  const { vmId } = useParams<{ vmId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const tabResolvido = tabParam ? (NAV_ALIASES[tabParam] ?? tabParam) : null
  const navSolicitado: NavId =
    tabResolvido && NAV_IDS.has(tabResolvido) ? (tabResolvido as NavId) : DEFAULT_NAV
  const setActiveNav = (id: NavId) => {
    const next = new URLSearchParams(searchParams)
    if (id === DEFAULT_NAV) next.delete('tab')
    else next.set('tab', id)
    setSearchParams(next, { replace: true })
  }
  const [isEditingName, setIsEditingName] = useState(false)
  const [editName, setEditName] = useState('')

  const {
    data: instance,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['instance', vmId],
    queryFn: () => instancesApi.getInstanceDetails(vmId!),
    enabled: !!vmId,
    refetchInterval: 30000,
  })

  // #923: com o firewall do CLUSTER desta instância desligado, regra de VM nenhuma filtra
  // nada — a aba Firewall some. Só o `false` explícito esconde: `null` (o backend não
  // conseguiu ler o cluster) e a falha desta consulta são "não sei", e a aba fica, com aviso.
  // Enquanto carrega (`undefined`), a aba também fica.
  const clusterFirewallQuery = useQuery({
    queryKey: ['cluster-firewall-status', vmId],
    queryFn: () => firewallApi.getClusterFirewallStatus(vmId!),
    enabled: !!vmId,
    staleTime: 60_000,
  })
  const clusterFirewallEnabled: boolean | null | undefined = clusterFirewallQuery.isError
    ? null
    : clusterFirewallQuery.data?.cluster_firewall_enabled
  const firewallDoClusterDesligado = clusterFirewallEnabled === false
  const navItems = firewallDoClusterDesligado
    ? SIDE_NAV_ITEMS.filter((item) => item.id !== 'firewall')
    : SIDE_NAV_ITEMS
  // Link salvo `?tab=firewall` com a aba escondida cai na aba padrão.
  const activeNav: NavId =
    navSolicitado === 'firewall' && firewallDoClusterDesligado ? DEFAULT_NAV : navSolicitado
  // E a URL acompanha: sem isso o `?tab=firewall` ficava lá, e um refetch que voltasse
  // `true` pularia para a aba sem o cliente ter clicado.
  useEffect(() => {
    if (navSolicitado === 'firewall' && firewallDoClusterDesligado) setActiveNav(DEFAULT_NAV)
  }, [navSolicitado, firewallDoClusterDesligado])

  const controlVmMutation = useMutation({
    mutationFn: async (action: string) =>
      apiClient.post(`/api/v1/end-customer/instances/${vmId}/action`, { action }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['instance', vmId] })
      toast.success(data.message || 'Ação executada')
    },
    onError: () => toast.error('Erro ao executar ação na VM'),
  })

  const renameMutation = useMutation({
    mutationFn: (newName: string) => instancesApi.updateInstance(vmId!, { name: newName }),
    onSuccess: () => {
      refetch()
      setIsEditingName(false)
      toast.success('Nome atualizado')
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Erro ao renomear'),
  })

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
            <ServerIcon className="h-7 w-7 animate-pulse text-slate-400" />
          </div>
          <p className="text-sm text-slate-500">Carregando instância...</p>
        </div>
      </div>
    )
  }

  if (error || !instance) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50">
            <ServerIcon className="h-8 w-8 text-red-400" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-slate-900">Erro ao carregar instância</h3>
          <p className="mb-6 text-sm text-slate-500">
            {error instanceof Error ? error.message : 'Instância não encontrada'}
          </p>
          <button
            onClick={() => navigate('/instances')}
            className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate-800"
          >
            Voltar para Instâncias
          </button>
        </div>
      </div>
    )
  }

  const px = instance.proxmox_details
  const isRunning = px?.status === 'running' || instance.status === 'running'
  const statusKey = px?.status || instance.status || 'creating'
  const currentStatus = STATUS_CONFIG[statusKey] || STATUS_CONFIG.creating
  const StatusIcon = currentStatus.icon
  // Issue #733: o card mostra UM endereço — a régua é a mesma dos comandos de acesso.
  // A comparação (em vez de `!!publicIp`) é proposital: o badge afirma "este endereço é o
  // público", então ele é derivado do que o card REALMENTE mostra. Se a precedência de
  // `resolveReachableIp` mudar um dia, o badge some junto em vez de passar a mentir.
  const publicIp = instance.public_ip?.ip_address || null
  const cardIpv4 = resolveReachableIp(instance)
  const cardIpv4IsPublic = !!cardIpv4 && cardIpv4 === publicIp

  const osName =
    instance.os_display_name ||
    px?.guest_os_info?.['pretty-name'] ||
    px?.guest_os_info?.name ||
    null
  const specs = [
    px?.cpu_cores ? `${px.cpu_cores} vCPU` : null,
    px?.memory_gb ? `${px.memory_gb} GB RAM` : null,
    px?.disk_gb ? `${px.disk_gb} GB Disk` : null,
    osName,
  ]
    .filter(Boolean)
    .join(' / ')

  return (
    <div className="mobile-page-padding">
      {/* Header */}
      <header className="mb-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ServerIcon className="h-7 w-7 flex-shrink-0 text-slate-400 sm:h-8 sm:w-8" />
              {isEditingName ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (editName.trim()) renameMutation.mutate(editName.trim())
                  }}
                  className="flex items-center gap-2"
                >
                  <input
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="border-b-2 border-slate-300 bg-transparent px-0 py-0 text-2xl font-bold text-gray-900 outline-none focus:border-slate-900 sm:text-3xl"
                    style={{ width: `${Math.max(editName.length, 8)}ch` }}
                  />
                  <button
                    type="submit"
                    disabled={renameMutation.isPending || !editName.trim()}
                    className="rounded-lg p-1 text-emerald-600 transition-colors hover:bg-emerald-50 disabled:opacity-40"
                  >
                    <CheckIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditingName(false)}
                    className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100"
                  >
                    <XIcon className="h-5 w-5" />
                  </button>
                </form>
              ) : (
                <h1 className="group flex items-center gap-2 text-2xl font-bold text-gray-900 sm:text-3xl">
                  <span className="truncate">{instance.name}</span>
                  <button
                    onClick={() => {
                      setEditName(instance.name)
                      setIsEditingName(true)
                    }}
                    className="rounded-lg p-1 text-slate-300 opacity-0 transition-all group-hover:opacity-100 hover:bg-slate-100 hover:text-slate-600"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                </h1>
              )}
            </div>
            <p className="mt-2 text-sm text-gray-600 sm:text-base">
              {specs || instance.product?.name || ''}
            </p>
            <div className="mt-2">
              <ResponsiveActionRow justify="start" className="w-full">
                <button
                  onClick={() => navigate('/instances')}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-1.5 text-sm text-slate-600 transition-colors hover:bg-slate-50"
                >
                  <ArrowLeftIcon className="h-4 w-4" /> Voltar
                </button>
                <span
                  className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium ${currentStatus.color}`}
                >
                  <StatusIcon className="h-3 w-3" /> {currentStatus.label}
                </span>
                {px?.uptime ? (
                  <span className="text-xs text-slate-400">Uptime: {formatUptime(px.uptime)}</span>
                ) : null}
              </ResponsiveActionRow>
            </div>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end">
            <ResponsiveActionRow justify="end" className="w-full sm:w-auto">
              {isRunning ? (
                <button
                  onClick={() => controlVmMutation.mutate('stop')}
                  disabled={controlVmMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                >
                  <SquareIcon className="h-4 w-4" /> Desligar
                </button>
              ) : (
                <button
                  onClick={() => controlVmMutation.mutate('start')}
                  disabled={controlVmMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
                >
                  <PlayIcon className="h-4 w-4" /> Iniciar
                </button>
              )}
              <button
                onClick={() => {
                  const w = 1280,
                    h = 800,
                    l = (window.screen.width - w) / 2,
                    t = (window.screen.height - h) / 2
                  window.open(
                    `/instances/${vmId}/console/popup`,
                    'vnc-console',
                    `width=${w},height=${h},left=${l},top=${t},resizable=yes`,
                  )
                }}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate-800"
              >
                <MonitorIcon className="h-4 w-4" /> Console
              </button>
            </ResponsiveActionRow>
          </div>
        </div>
      </header>

      {/* Provisionamento que não terminou (#803).
          Faixa, e não badge no status: o status desta VM é o REAL (a reconciliação
          o conciliou com o cluster) e não é ele que está errado — o que falta é a
          cauda da criação. O texto é o único que o cliente recebe; o motivo técnico
          fica no backend, para o suporte. Logo abaixo do cabeçalho de propósito:
          quem abre o detalhe entra no topo da página e o lê antes de agir. */}
      {provisionamentoIncompleto(instance) && (
        <div
          data-testid="aviso-provisionamento-incompleto"
          className="mb-6 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3"
        >
          <AlertTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              {AVISO_PROVISIONAMENTO_INCOMPLETO.titulo}
            </p>
            <p className="mt-0.5 text-sm text-amber-800">
              {AVISO_PROVISIONAMENTO_INCOMPLETO.mensagem}
            </p>
            {/* "Desde quando" é a primeira pergunta de quem lê o aviso — e é a única
                razão de o backend expor a data. Sem ela, a frase some. */}
            {desdeQuandoIncompleto(instance) && (
              <p className="mt-1 text-xs text-amber-700">
                Interrompido em {desdeQuandoIncompleto(instance)}.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Info cards */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5">
        {[
          {
            id: 'ipv4',
            label: 'IPv4',
            value: cardIpv4 || '—',
            icon: GlobeIcon,
            copyable: !!cardIpv4,
            badge: cardIpv4IsPublic ? 'Público fixo' : undefined,
          },
          {
            id: 'cpu',
            label: 'CPU',
            value: px?.cpu_cores ? `${px.cpu_cores} vCPUs` : '—',
            icon: CpuIcon,
            extra: px?.cpu_usage != null ? `${px.cpu_usage.toFixed(1)}%` : undefined,
          },
          {
            id: 'memoria',
            label: 'Memória',
            value: px?.memory_gb ? `${px.memory_gb} GB` : '—',
            icon: DatabaseIcon,
            extra:
              px?.memory_usage_percent != null
                ? `${px.memory_usage_percent.toFixed(1)}%`
                : undefined,
          },
          // "Disco total": este número é a SOMA (sistema + block storages anexados).
          // Rotulá-lo só de "Disco" ao lado de uma aba que mostra "Disco do sistema:
          // 40 GB" reproduz na mesma tela a ambiguidade que a #759 ataca.
          {
            id: 'disco',
            label: 'Disco total',
            value: px?.disk_gb ? `${px.disk_gb} GB` : '—',
            icon: HardDriveIcon,
          },
          { id: 'os', label: 'OS', value: osName || '—', icon: MonitorIcon },
        ].map((item) => {
          const CardIcon = item.icon
          return (
            <div
              key={item.id}
              data-testid={`card-${item.id}`}
              className="rounded-2xl border border-slate-200/60 bg-white px-4 py-3 shadow-sm"
            >
              <div className="mb-1 flex items-center gap-1.5">
                <CardIcon className="h-3 w-3 text-slate-400" />
                <span className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
                  {item.label}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="truncate text-sm font-medium text-slate-900">{item.value}</span>
                {item.copyable && <CopyButton text={item.value} />}
                {item.extra && <span className="ml-auto text-xs text-slate-400">{item.extra}</span>}
              </div>
              {item.badge && (
                <span className="mt-1 inline-block rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap text-blue-700">
                  {item.badge}
                </span>
              )}
            </div>
          )
        })}
      </div>

      {/* Two-column layout */}
      <div className="flex gap-6">
        {/* Side Navigation */}
        <nav className="hidden w-48 flex-shrink-0 md:block">
          <div className="sticky top-16 rounded-2xl border border-slate-200/60 bg-white p-3 shadow-sm">
            <ul className="space-y-0.5">
              {navItems.map((item) => {
                const NavIcon = item.icon
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => setActiveNav(item.id)}
                      className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm transition-colors ${
                        activeNav === item.id
                          ? item.danger
                            ? 'bg-red-50 font-medium text-red-600'
                            : item.warning
                              ? 'bg-amber-50 font-medium text-amber-600'
                              : 'bg-slate-900 font-medium text-white'
                          : item.danger
                            ? 'text-red-400 hover:bg-red-50 hover:text-red-600'
                            : item.warning
                              ? 'text-amber-400 hover:bg-amber-50 hover:text-amber-600'
                              : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                      }`}
                    >
                      <NavIcon className="h-4 w-4" /> {item.label}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        </nav>

        {/* Content área */}
        <div className="min-w-0 flex-1">
          {activeNav === 'graphs' && <GraphsSection instanceId={vmId!} />}
          {activeNav === 'access' && <AccessSection instance={instance} refetch={refetch} />}
          {activeNav === 'power' && <PowerSection instance={instance} refetch={refetch} />}
          {activeNav === 'resources' && <ResourcesSection instance={instance} refetch={refetch} />}
          {activeNav === 'networking' && <NetworkTab instance={instance} />}
          {activeNav === 'firewall' && (
            <FirewallTab instance={instance} clusterFirewallEnabled={clusterFirewallEnabled} />
          )}
          {activeNav === 'backups' && <BackupsSection vmId={vmId!} />}
          {activeNav === 'snapshots' && <SnapshotsTab instanceId={vmId!} />}
          {activeNav === 'history' && <HistorySection vmId={vmId!} />}
          {activeNav === 'rebuild' && <RebuildSection instance={instance} refetch={refetch} />}
          {activeNav === 'destroy' && <DestroySection instance={instance} />}
        </div>
      </div>
    </div>
  )
}
